import { useState, useEffect, useCallback } from "react";
import AvoirsModel from "../models/AvoirsModel";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

export const base_url = "http://localhost:8000/api/v1/";

// ✅ Hook pour récupérer les avoirs avec pagination et filtres


export function useGetAvoirs(
    initialStatut = "Tous",
    initialDateRange = null,
    customToken = null
) {
    const [avoirs, setAvoirs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [pagination, setPagination] = useState({
        total: 0,
        per_page: 6,
        current_page: 1,
        last_page: 1,
    });
    const [statut, setStatut] = useState(initialStatut);
    const [dateRange, setDateRange] = useState(
        initialDateRange || { startDate: null, endDate: null }
    );
    const [clientNom, setClientNom] = useState("");
    const [numeroAvoir, setNumeroAvoir] = useState("");

    const reduxToken = useSelector((state) => state.auth.token);
    const token = customToken || reduxToken;
    const navigate = useNavigate();

    const fetchAvoirs = useCallback(
        async (
            customPage = 1,
            filterStatut = statut,
            range = dateRange,
            client = clientNom,
            numero = numeroAvoir
        ) => {
            if (!token) {
                navigate("/login");
                return;
            }

            setLoading(true);
            try {
                const url = new URL(`${base_url}avoirs`);
                url.searchParams.append("page", customPage);
                url.searchParams.append("per_page", pagination.per_page);
                url.searchParams.append("statut", filterStatut || "Tous");

                if (range.startDate)
                    url.searchParams.append(
                        "date_debut_emission",
                        range.startDate.toISOString().split("T")[0]
                    );
                if (range.endDate)
                    url.searchParams.append(
                        "date_fin_emission",
                        range.endDate.toISOString().split("T")[0]
                    );

                if (client.trim() !== "") url.searchParams.append("client", client.trim());
                if (numero.trim() !== "") url.searchParams.append("numero_avoir", numero.trim());

                const response = await fetch(url, {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                });

                if (!response.ok) throw new Error("Erreur lors de la récupération des avoirs");

                const data = await response.json();
                console.log("API response:", data); // <-- debug

                // Correction ici : accéder à data.avoirs.data
                const avoirsFormatés = data.avoirs.map((item) => new AvoirsModel(item));



                setAvoirs(avoirsFormatés);

                setPagination({
                    total: data.total,
                    per_page: data.per_page,
                    current_page: data.current_page,
                    last_page: Math.ceil(data.total / data.per_page), // calculer si le backend ne renvoie pas last_page
                });
            } catch (error) {
                console.error("Erreur:", error);
                setAvoirs([]);
            } finally {
                setLoading(false);
            }
        },
        [token, navigate, pagination.per_page, statut, dateRange, clientNom, numeroAvoir]
    );


    useEffect(() => {
        fetchAvoirs(page, statut, dateRange, clientNom, numeroAvoir);
    }, [fetchAvoirs, page, statut, dateRange, clientNom, numeroAvoir]);

    return {
        avoirs,
        loading,
        pagination,
        page,
        setPage,
        fetchAvoirs,
        setAvoirs,
        setLoading,
        setPagination,
        statut,
        setStatut,
        dateRange,
        setDateRange,
        clientNom,
        setClientNom,
        numeroAvoir,
        setNumeroAvoir,
    };
}

// ✅ Ajouter un avoir
export async function addAvoir(avoirData) {
    const token = localStorage.getItem("authTokenUtilisateur");
    if (!token) throw new Error("Utilisateur non authentifié");

    const payload = {
        facture_id: avoirData.facture_id,
        numero: avoirData.numero?.trim() || "",
        date_emission: avoirData.date_emission,
        statut: avoirData.statut || "brouillon",
        titre: avoirData.titre || null,
        notes_client: avoirData.notes_client || null,
        notes_internes: avoirData.notes_internes || null,
        sous_total_centimes: avoirData.sous_total_centimes || 0,
        remise_centimes: avoirData.remise_centimes || 0,
        tva_centimes: avoirData.tva_centimes || 0,
        total_centimes: avoirData.total_centimes || 0,
        acompte_centimes: avoirData.acompte_centimes || 0,
        reste_du_centimes: avoirData.reste_du_centimes ?? Math.max(avoirData.total_centimes - (avoirData.acompte_centimes || 0), 0),
        type_paiement: avoirData.type_paiement || null,
        devise: avoirData.devise || "€",
        lignes: (avoirData.lignes || []).map((ligne) => ({
            designation: ligne.designation?.trim() || "",
            quantite: parseFloat(ligne.quantite) || 1,
            unite: ligne.unite || null,
            prix_unitaire_centimes: parseInt(ligne.prix_unitaire_centimes) || 0,
            taux_tva: parseFloat(ligne.taux_tva) || 0,
            taux_remise: parseFloat(ligne.taux_remise) || 0,
            total_ligne_centimes: parseInt(ligne.total_ligne_centimes) || 0,
        })),
    };

    const response = await fetch(`${base_url}avoirs`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
    });

    let data;
    try {
        data = await response.json();
    } catch (err) {
        throw new Error("Le serveur a renvoyé une réponse invalide");
    }

    if (!response.ok) throw new Error(data.message || "Erreur lors de la création de l'avoir");

    return data;
}

// services/AvoirService.js

export async function getAvoir(id) {
    const token = localStorage.getItem("authTokenUtilisateur");
    if (!token) throw new Error("Utilisateur non authentifié");

    if (!id || isNaN(parseInt(id))) throw new Error("ID d'avoir invalide");

    const response = await fetch(`${base_url}avoirs/${id}`, {
        method: "GET",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
        },
    });

    let data;
    try {
        data = await response.json();
    } catch (err) {
        throw new Error("Le serveur a renvoyé une réponse invalide");
    }

    if (!response.ok) throw new Error(data.message || "Erreur lors de la récupération de l'avoir");

    // Crée et renvoie le modèle complet
    return new AvoirsModel(data.avoir);
}


// ✅ Mettre à jour un avoir
export async function updateAvoir(id, avoirData) {
    const token = localStorage.getItem("authTokenUtilisateur");
    if (!token) throw new Error("Utilisateur non authentifié");

    if (!id || isNaN(parseInt(id))) throw new Error("ID d'avoir invalide");

    const response = await fetch(`${base_url}avoirs/${id}`, {
        method: "PUT",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(avoirData),
    });

    let data;
    try {
        data = await response.json();
    } catch (err) {
        throw new Error("Le serveur a renvoyé une réponse invalide");
    }

    if (!response.ok) throw new Error(data.message || "Erreur lors de la mise à jour de l'avoir");

    return data;
}

// ✅ Supprimer un avoir
export async function deleteAvoir(id) {
    const token = localStorage.getItem("authTokenUtilisateur");
    if (!token) throw new Error("Utilisateur non authentifié");

    const response = await fetch(`${base_url}avoirs/${id}`, {
        method: "DELETE",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
        },
    });

    let data;
    try {
        data = await response.json();
    } catch (err) {
        throw new Error("Le serveur a renvoyé une réponse invalide");
    }

    if (!response.ok) throw new Error(data.message || "Erreur lors de la suppression");

    return data;
}
